<template>
  <div class="HotelBookingBar">
    <ai-booking-bar
      ref="bookingBar"
      v-model:dates="currentDates"
      v-model:include-flights="includeFlights"
      v-model:rooms-and-guests="currentRoomsAndGuests"
      :bottom="bottom"
      :dates-errors="datesError"
      :disabled="loading"
      :manage-flights="isFlightHotelAvailable"
      :minimum-stay-duration="minimumStayDuration"
      :mobile-items="mobileItems"
      :mobile-titles="{
        origins: $t('homepage.hero.bar.mobile.origin.title'),
      }"
      :rooms-and-guests-errors="childrenAgeMobileError"
      :sticky="sticky"
      :children-max-age="childrenMaxAge"
      @submit="toBooking"
      @reset-all="reset">
      <template #input-before>
        <origin-autocomplete
          v-if="isFlightHotelAvailable && includeFlights"
          v-model="origin"
          :errors="originError" />
      </template>

      <!-- Mobile -->
      <template #mobile-default-before="slotProps">
        <ai-field-input
          v-if="isFlightHotelAvailable && includeFlights"
          :errors="originError"
          :label="$t('homepage.hero.bar.mobile.origin.label')"
          :model-value="origin.text"
          :placeholder="$t('homepage.hero.bar.mobile.origin.placeholder')"
          class="HotelBookingBar-mobileInput"
          name="origin-mobile"
          readonly
          @click="
            slotProps.setPage('origins');
            delayedFocusTo('leaving-from');
          "
          @keyup.enter="
            slotProps.setPage('origins');
            delayedFocusTo('leaving-from');
          ">
          <template #right>
            <ai-icon
              :size="18"
              class="AiBookingMobileForm-icon"
              name="location" />
          </template>
        </ai-field-input>
      </template>

      <template #mobile-after>
        <ai-separator :margin="2" />

        <div v-if="bestOffer && !bestOffer?.empty">
          <ai-amount
            :amount="convertedBestOffer.amount"
            :symbol="convertedBestOffer.currency?.code"
            as="div" />
          <ai-typo as="p" variant="overline-01">
            {{ taxLabel }}
          </ai-typo>
          <ai-typo
            as="div"
            class="HotelBookingBar-resume"
            variant="overline-01">
            {{ $t('hotel.searchBlock.resumeBooking.night', { count: nights }) }}
            -
            {{
              $t('hotel.searchBlock.resumeBooking.guests', {
                count: adults || 2,
              })
            }}
          </ai-typo>
        </div>

        <div
          v-if="error || bestOffer?.empty"
          class="HotelBookingBar-status HotelBookingBar-status--mobile">
          <ai-icon class="HotelBookingBar-statusIcon" name="warning" />
          <ai-typo as="span" variant="paragraph-04-regular">
            {{ errorLabel }}
          </ai-typo>
        </div>

        <ai-button
          :label="$t('hotel.searchBlock.searchCtaLabel')"
          full-width
          @click="toBooking" />
      </template>

      <!-- Mobile - Origin page -->
      <template #origins>
        <origin-autocomplete
          v-model="origin"
          v-if="isFlightHotelAvailable"
          :errors="originError"
          class="HotelBookingBar-mobilePage-input"
          hide-suggestions
          @origins-fetched="origins => (originSuggestions = origins)" />

        <origin-suggestion
          v-for="(suggestion, index) of originSuggestions"
          :id="suggestion.id"
          :key="`${suggestion.type}-${index}`"
          :country="suggestion.country"
          :full-name="suggestion.fullName"
          :name="suggestion.name"
          :type="suggestion.type"
          @click="setOriginSuggestion" />
      </template>

      <template #mobile-submit>
        <ai-button
          :label="$t('hotel.searchBlock.searchCtaLabel')"
          :loading="loading"
          @click="toBooking" />
      </template>

      <template v-if="error || bestOffer?.empty || loading" #errors>
        <div
          v-if="error || bestOffer?.empty"
          class="HotelBookingBar-status HotelBookingBar-status--mobile">
          <ai-icon class="HotelBookingBar-statusIcon" name="warning" />
          <ai-typo as="span" variant="paragraph-04-regular">
            {{ errorLabel }}
          </ai-typo>
        </div>

        <div
          v-if="loading"
          class="HotelBookingBar-status HotelBookingBar-status--mobile">
          <ai-icon
            class="HotelBookingBar-statusIcon HotelBookingBar-statusIcon--stratos"
            name="hourglass" />
          <ai-typo as="span" variant="paragraph-04-regular">
            {{ $t('hotel.searchBlock.loadingLabel') }}
          </ai-typo>
        </div>
      </template>

      <!-- Desktop -->
      <template #action>
        <div class="HotelBookingBar-desktopAction">
          <div v-if="displayAmountBlock">
            <ai-amount
              :amount="convertedBestOffer.amount"
              :symbol="convertedBestOffer.currency?.code"
              as="div" />
            <ai-typo as="p" variant="overline-01">
              {{ taxLabel }}
            </ai-typo>
            <ai-typo as="div" variant="overline-01">
              {{
                $t('hotel.searchBlock.resumeBooking.night', { count: nights })
              }}
              -
              {{
                $t('hotel.searchBlock.resumeBooking.guests', {
                  count: (adults || 2) + childrenAges.length,
                })
              }}
            </ai-typo>
          </div>

          <div v-else-if="loading" class="HotelBookingBar-status">
            <ai-icon name="hourglass" />
            <ai-typo as="span" variant="paragraph-04-regular">
              {{ $t('hotel.searchBlock.loadingLabel') }}
            </ai-typo>
          </div>

          <div
            v-else-if="error || bestOffer?.empty"
            class="HotelBookingBar-status">
            <ai-icon class="HotelBookingBar-statusIcon" name="warning" />
            <ai-typo as="span" variant="paragraph-04-regular">
              {{ errorLabel }}
            </ai-typo>
          </div>

          <ai-button
            :label="$t('hotel.searchBlock.searchCtaLabel')"
            :loading="loading"
            @click="toBooking" />
        </div>
      </template>
    </ai-booking-bar>

    <!-- Custom sticky mobile widget -->
    <div v-if="sticky" ref="widget" class="HotelBookingBar-widget">
      <div
        v-if="error || bestOffer?.empty"
        class="HotelBookingBar-status HotelBookingBar-widget-header">
        <ai-icon class="HotelBookingBar-statusIcon" name="warning" />
        <ai-typo as="span" variant="paragraph-04-regular">
          {{ errorLabel }}
        </ai-typo>
      </div>
      <div
        v-else-if="!loading && !error && bestOffer && !bestOffer?.empty"
        class="HotelBookingBar-widget-header">
        <ai-amount
          :amount="bestOfferPrice"
          :symbol="bestOfferCurrency"
          as="div" />
        <ai-typo as="p" variant="overline-01">
          {{ taxLabel }}
        </ai-typo>
        <ai-typo as="div" variant="overline-01">
          {{ $t('hotel.searchBlock.resumeBooking.night', { count: nights }) }}
          -
          {{
            $t('hotel.searchBlock.resumeBooking.guests', {
              count: (adults || 2) + childrenAges.length,
            })
          }}
        </ai-typo>
      </div>

      <div class="HotelBookingBar-widget-body">
        <div class="HotelBookingBar-widget-recap">
          <ai-button variant="tertiary">
            <ai-icon
              :size="32"
              class="HotelBookingBar-widget-icon"
              name="edit"
              @click="bookingBar?.openMobileForm?.()" />
          </ai-button>

          <div class="HotelBookingBar-widget-infos">
            <ai-typo as="span" variant="overline-01">
              {{ mobileOriginLabel }}
            </ai-typo>
            <ai-typo as="span" variant="overline-01">{{ datesLabel }}</ai-typo>
            <ai-typo as="span" variant="overline-01">
              {{ roomsAndGuestsLabel }}
            </ai-typo>
          </div>
        </div>

        <ai-button
          :label="$t('booking.bar.mobile.form.confirm')"
          @click="toBooking" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ApolloError } from '@apollo/client/errors';
import { useApolloClient } from '@vue/apollo-composable';
import { differenceInCalendarDays, differenceInDays, format } from 'date-fns';

import type { UnconfiguredGuests } from '~/domains/booking';
import { useBookingStore, useBookingUrlGenerator } from '~/domains/booking';
import {
  useFlightHotelAvailability,
  useFlightHotelUrlGenerator,
  useFlightHotelValidator,
} from '~/domains/feature';
import type { SearchOrigin as GQLSearchOrigin } from '~/domains/graphql';
import {
  BestOfferDocument,
  SearchDestinationResultType,
  ValidateRoomsDocument,
  useGlobalConfigurationAsyncData,
} from '~/domains/graphql';
import type { SearchOrigin } from '~/domains/search';
import type { Props as OriginSuggestionProps } from '~/domains/search/components/OriginSuggestion.vue';
import { useUserStore } from '~/domains/user';
import type AiBookingBar from '~/domains/ux/organisms/AiBookingBar/AiBookingBar.vue';
import type { MobileItem } from '~/domains/ux/organisms/AiBookingBar/AiBookingBar.vue';
import { FORMATS, formatDate, delayedFocusTo } from '~/helpers';

type Props = {
  bottom?: boolean;
  dates: Date[];
  destinationId: string;
  minimumStayDuration?: number;
  name: string;
  ridcode: string;
  referenceId?: string | null;
  roomsAndGuests: UnconfiguredGuests[];
  childrenMaxAge?: number;
  sticky?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  bottom: false,
  minimumStayDuration: undefined,
  sticky: false,
  childrenMaxAge: undefined,
  referenceId: undefined,
});

type Emits = {
  (event: 'update:dates', dates: Props['dates']): void;
  (
    event: 'update:roomsAndGuests',
    roomsAndGuests: Props['roomsAndGuests'],
  ): void;
  (event: 'bestOfferEvent', values: { price: number; currency: string }): void;
};
const emits = defineEmits<Emits>();

const route = useRoute();

const bookingStore = useBookingStore();
const userStore = useUserStore();
const { data: globalConfigurationData } = useGlobalConfigurationAsyncData();
const { redirectToAvailability } = useExternalNavigation();
const { bookingURL } = useBookingUrlGenerator();
const { isFlightHotelAvailable } = useFlightHotelAvailability();
const { datesError, originError, validateFlightHotelParams } =
  useFlightHotelValidator();

const origin = ref<SearchOrigin>({ text: '' });

const originSuggestions = ref<GQLSearchOrigin[]>([]);

const currentRoomsAndGuests = computed({
  get() {
    return props.roomsAndGuests ?? [];
  },
  set(roomsAndGuests: Props['roomsAndGuests']) {
    emits('update:roomsAndGuests', roomsAndGuests);
  },
});

const currentDates = computed<Props['dates']>({
  get() {
    if (!props.dates) return [] as Date[];
    return props.dates as Date[];
  },
  set(dates) {
    emits('update:dates', dates);
  },
});

const mobileOriginLabel = computed(() =>
  origin?.value.text === ''
    ? t('booking.bar.mobile.selectOrigin')
    : origin?.value.text,
);

const mobileOriginIsBold = computed(
  () => mobileOriginLabel?.value !== t('booking.bar.mobile.selectOrigin'),
);

const mobileItems = computed<MobileItem[]>(() => {
  const originItem = {
    icon: 'location',
    key: 'origin',
    label: mobileOriginLabel.value,
    position: 'before',
    bold: mobileOriginIsBold.value,
  } as MobileItem;

  return includeFlights.value ? [originItem] : [];
});

const bookingBar = ref<typeof AiBookingBar>();

const adults = computed(() => currentRoomsAndGuests.value[0]?.adults ?? 0);
const dateIn = computed(() =>
  formatDate(currentDates.value?.[0], 'yyyy-MM-dd'),
);
const nights = computed(() =>
  Math.abs(
    differenceInCalendarDays(
      currentDates.value?.[0] ?? 0,
      currentDates.value?.[1] ?? 0,
    ),
  ),
);
const childrenAges = computed(
  () => currentRoomsAndGuests.value[0]?.childrenAges ?? [],
);
const includeFlights = ref(false);

const bestOfferQueryPayload = computed(() => ({
  adults: currentRoomsAndGuests.value[0]?.adults ?? 2,
  childrenAges: currentRoomsAndGuests.value[0]?.childrenAges ?? [],
  dateIn: dateIn.value,
  hotelCode: props.ridcode,
  nights: nights.value,
}));

const canFetchBestOffer = computed(() => {
  return Boolean(dateIn.value && nights.value && props.ridcode);
});

const redirectToBooking = ref(false);
const error = ref<ApolloError | Error>();
const loading = ref(false);

const { client } = useApolloClient();
const { t, locale } = useI18n();
const { generateUrl: generateExpediaUrl } = useFlightHotelUrlGenerator(
  locale,
  'hotel',
);

const errorLabel = computed<string>(() => {
  if (!canFetchBestOffer.value) {
    return t('hotel.searchBlock.travelDateErrorLabel');
  }

  return t('hotel.searchBlock.noAvailabilityLabel');
});

const areAllTaxesIncluded = useAreAllTaxesIncluded();
const bestOffer = ref();
const bestOfferPrice = computed(
  () =>
    (areAllTaxesIncluded.value
      ? bestOffer.value?.price?.afterTax
      : bestOffer.value?.price?.hotelKeeper) ?? 0,
);
const bestOfferCurrency = computed(() => bestOffer.value?.currency);
const currentCurrency = computed(() => bookingStore.currentCurrency);
const displayAmountBlock = computed(
  () =>
    !loading.value &&
    !error.value &&
    bestOffer.value &&
    !bestOffer?.value?.empty &&
    !redirectToBooking.value,
);

const convertedBestOffer = useConvertToUserCurrency(
  bestOfferPrice,
  bestOfferCurrency,
  currentCurrency,
);

const taxLabel = computed<string>(() =>
  areAllTaxesIncluded.value
    ? t('booking.roomRate.freeTaxLabel')
    : t('booking.roomRate.taxLabel'),
);

const childrenAgeMobileError = ref<
  { roomIndex: number; childrenAges: number[] }[]
>([]);

const mobileAction = () => {
  if (includeFlights.value) {
    validateFlightHotelParams(currentDates.value, undefined, origin.value);
  }

  bookingBar.value?.openMobileForm?.();
};

const reset = () => {
  origin.value.text = '';
  childrenAgeMobileError.value = [];
  includeFlights.value = false;
};

const setOriginSuggestion = (suggestion: OriginSuggestionProps) => {
  origin.value = {
    text: suggestion.name,
    type: suggestion.type,
  };

  if (typeof bookingBar.value?.setPage === 'function') {
    bookingBar.value.setPage(undefined);
  }
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const toBooking = async () => {
  childrenAgeMobileError.value = [];

  if (
    currentRoomsAndGuests.value.some(room =>
      room.childrenAges.some(age => typeof age !== 'number'),
    )
  ) {
    for (
      let roomIndex = 0;
      roomIndex < currentRoomsAndGuests.value.length;
      roomIndex++
    ) {
      const room = currentRoomsAndGuests.value[roomIndex];
      const roomError = { roomIndex, childrenAges: [] } as {
        roomIndex: number;
        childrenAges: number[];
      };

      for (let i = 0; i < room.childrenAges.length; i++) {
        if (typeof room.childrenAges[i] === 'number') continue;

        roomError.childrenAges.push(i);
      }

      childrenAgeMobileError.value.push(roomError);
    }

    return;
  }

  if (isFlightHotelAvailable && includeFlights.value) {
    validateFlightHotelParams(currentDates.value, undefined, origin.value);

    if (datesError.value || originError.value) {
      return;
    }

    const url = generateExpediaUrl(
      origin.value,
      {
        text: props.name,
        type: SearchDestinationResultType.Hotel,
        ridcode: props.ridcode,
        destinationId: props.destinationId,
      },
      {
        dateIn: formatDate(currentDates.value[0], FORMATS.apiFormat),
        nights: differenceInDays(currentDates.value[1], currentDates.value[0]),
        roomsAndGuests: currentRoomsAndGuests.value,
      },
      'hotel',
    );

    window.open(url, '_self');

    return;
  }

  if (!canFetchBestOffer.value) {
    error.value = new ApolloError({});
    return;
  }

  redirectToBooking.value = true;

  await bookNow();
};

const datesLabel = computed(() => {
  if (
    !currentDates.value ||
    (Array.isArray(currentDates.value) && currentDates.value.length <= 0)
  )
    return t('booking.bar.mobile.anyDates');

  const rawFirstDate = (currentDates.value as Date[])[0];
  const rawSecondDate = (currentDates.value as Date[])[1];

  const firstDate = format(rawFirstDate, 'dd LLL');
  const secondDate = format(rawSecondDate, 'dd LLL');

  return `${firstDate} - ${secondDate}`;
});

const roomsAndGuestsLabel = computed(() => {
  const rooms = currentRoomsAndGuests.value.length;
  const guests = currentRoomsAndGuests.value.reduce(
    (sum, room) => sum + room.adults + (room.childrenAges.length ?? 0),
    0,
  );

  if (!rooms || !guests) return t('booking.bar.mobile.defaultGuests');

  const roomsLabel = t('booking.bar.inputs.roomAndGuest.room', {
    count: rooms,
  });

  const guestsLabel = t('booking.bar.inputs.roomAndGuest.guest', {
    count: guests,
  });

  return t('booking.bar.inputs.roomAndGuest.fullValue', {
    guests: guestsLabel,
    rooms: roomsLabel,
  });
});

const shouldOffsetBodyForWidget = computed(() => props.bottom && props.sticky);
const widget = ref<HTMLDivElement>();

const bookNow = async () => {
  loading.value = true;

  const guests = currentRoomsAndGuests.value?.length
    ? currentRoomsAndGuests.value
    : [{ adults: 2, childrenAges: [] }];

  const bookingConfig =
    globalConfigurationData.value?.globalConfiguration?.booking;
  if (
    bookingConfig?.useExternalRoomsPage &&
    bookingConfig.externalSearchPageUrl &&
    props.referenceId
  ) {
    await redirectToAvailability(bookingConfig.externalSearchPageUrl, {
      hotelReferenceId: props.referenceId,
      dates: currentDates.value,
      roomsAndGuests: guests,
      isLogged: userStore.isLogged,
    });
    return;
  }

  const validRooms = await client.query({
    query: ValidateRoomsDocument,
    variables: {
      dateIn: dateIn.value,
      nights: nights.value,
      hotelCode: props.ridcode,
      rooms: guests,
    },
    fetchPolicy: 'network-only',
  });

  loading.value = false;

  if (!validRooms.data.validateRooms) {
    error.value = new Error('invalid rooms');
    return;
  }

  bookingBar.value?.closeMobileForm?.();

  navigateTo(bookingURL(guests, props.ridcode, dateIn.value, nights.value));
};

const checkBestOffer = async (onload = false) => {
  if (
    !bestOfferQueryPayload.value.dateIn ||
    !bestOfferQueryPayload.value.nights
  ) {
    return;
  }

  loading.value = true;

  try {
    const result = await client.query({
      query: BestOfferDocument,
      variables: bestOfferQueryPayload.value,
      fetchPolicy: 'network-only',
    });

    bestOffer.value = result.data.bestOffer;
    if (onload) {
      const values = {
        price: bestOfferPrice.value,
        currency: bestOfferCurrency.value,
      };
      emits('bestOfferEvent', values);
    }
    error.value = undefined;
  } catch (e) {
    error.value = e as ApolloError;
  } finally {
    loading.value = false;
  }
};

const checkIncludeFlights = () => {
  includeFlights.value = route.query.includeFlights === 'true';
};

watch(shouldOffsetBodyForWidget, shouldOffset => {
  if (!widget.value) return;

  const height = widget.value?.getBoundingClientRect()?.height;
  const footer = window.document.querySelector<HTMLElement>('footer');

  if (!footer) return;

  if (shouldOffset) {
    footer.style.marginBottom = `${height}px`;
  } else {
    footer.style.marginBottom = '';
  }
});

watch(currentRoomsAndGuests, () => checkBestOffer());
watch(currentDates, () => {
  checkBestOffer();
  datesError.value = undefined;
});
watch(origin, () => {
  originError.value = undefined;
});
watch(includeFlights, isFlightIncluded => {
  if (!isFlightIncluded) {
    originError.value = undefined;
    datesError.value = undefined;
  }
});

onMounted(() => {
  checkIncludeFlights();
  checkBestOffer(true);
});
</script>

<style lang="scss" scoped>
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/mq';
@use '@/assets/styles/utilities/colors';

.HotelBookingBar-resume {
  text-transform: uppercase;
  margin-bottom: constants.$margin-02;
}

.HotelBookingBar-desktopAction {
  display: flex;
  flex-direction: row;
  gap: constants.$margin-03;
}

.HotelBookingBar-status {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: constants.$inner-02;

  &--mobile {
    margin-bottom: constants.$margin-02;

    @media (mq.$from-large) {
      display: none;
    }
  }
}

.HotelBookingBar-statusIcon {
  color: colors.$red-700;
  fill: colors.$red-700;

  &--stratos {
    color: colors.$stratos-700;
    fill: colors.$stratos-700;
  }
}

.HotelBookingBar-widget {
  position: fixed;
  z-index: 20;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: colors.$gold-100;
  padding: constants.$padding-02 constants.$padding-01;
  display: flex;
  flex-direction: column;

  @media (mq.$from-medium) {
    display: none;
  }
}

.HotelBookingBar-widget-header {
  margin-bottom: constants.$inner-03;
}

.HotelBookingBar-widget-body {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.HotelBookingBar-widget-recap {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.HotelBookingBar-widget-icon {
  color: colors.$gold-900;
  margin-right: constants.$inner-02;
}

.HotelBookingBar-widget-infos {
  display: flex;
  flex-direction: column;
  text-align: left;
  text-transform: uppercase;
}

.HotelBookingBar-mobilePage-input {
  :deep(.AiFieldInput-input) {
    @supports (-webkit-touch-callout: none) {
      font-size: 1.6rem;
    }
  }
}

.HotelBookingBar-mobileInput {
  margin-bottom: constants.$margin-04;
}
</style>
